import { Link } from 'react-router-dom';
import MentorPortalService, { MentorMenteeCard as MenteeCard } from './MentorPortalService';
import { formatDayMonthYear, initialsFromName, joinPresent } from './mentorDashboardFormat';

type Props = {
    mentee: MenteeCard;
    toneIndex: number;
    onLog?: () => void;
};

const AVATAR_TONES = ['bg-primary bg-opacity-25', 'bg-warning-subtle', 'bg-primary-subtle', 'bg-warning', 'bg-danger-subtle'];

const GOALS_PLACEHOLDER = 'Goals will appear when the mentee submits registration';

const MentorMenteeCard = ({ mentee, toneIndex, onLog }: Props) => {
    const trackNames = (mentee.tracks || [])
        .map((track) => track.trackName)
        .filter((name): name is string => !!name);
    // Campus name, roll number, and placement season are not on GET /mentor/mentees — omit.
    const identityLine = joinPresent([mentee.program, mentee.batchYear]);
    const metaLine = joinPresent([
        mentee.program ? `Program: ${mentee.program}` : null,
        mentee.batchYear != null ? `Batch: ${mentee.batchYear}` : null,
    ]);
    const lastSession = formatDayMonthYear(mentee.lastSessionDate) || '—';
    const uuid = mentee.menteeUuid;
    const profileHref = MentorPortalService.studentProfileHref(uuid);
    const goalText = mentee.goals ? String(mentee.goals).trim() : '';
    const dod = mentee.dodProgress;
    const tone = AVATAR_TONES[Math.abs(toneIndex) % AVATAR_TONES.length];

    return (
        <div className="col-md-4">
            <div className='card border-0 shadow-sm rounded-3 bg-white h-100 overflow-hidden'>
                <div className='p-3 border-bottom border-light-subtle'>
                    <div className='d-flex align-items-start gap-3'>                        
                        <span className={`rounded-circle border border-primary border-opacity-25 flex-shrink-0 d-flex justify-content-center align-items-center font-600 ${tone}`} style={{width:'46px', height:'46px'}}>{initialsFromName(mentee.menteeName)}</span>
                        <div className="flex-grow-1">
                        {mentee.menteeName ? <h2 className="fw-bold font-14 mb-0">{mentee.menteeName}</h2> : null}
                        {identityLine ? <div className="text-secondary small font-12">{identityLine}</div> : null}
                        

                        {trackNames.length > 0 ? (
                        <div className="d-flex gap-1 mt-2">
                            {trackNames.map((name) => (
                                <span key={name} className="badge bg-light text-secondary font-11 border border-light-subtle">
                                    {name}
                                </span>
                            ))}
                        </div>
                    ) : null}

                      </div>
                      <span className="badge bg-success bg-opacity-10 text-success fw-bold py-1 px-2 font-11 rounded-pill">Active</span>
                    </div>

                    <div className='bg-light bg-opacity-50 border border-light-subtle rounded-3 p-3 font-13 text-black mt-3 lh-base'>
                        <strong >Mentorship goals: </strong>
                        {goalText || GOALS_PLACEHOLDER}
                    </div>

                    <div className="my-3 font-12 text-muted d-flex flex-wrap gap-2">
                      {metaLine ? <span className="badge bg-secondary-subtle text-black font-12 font-500 rounded-pill">{metaLine}</span> : null} 
                      <span className="badge bg-secondary-subtle text-black font-12 font-500 rounded-pill"><b className='font-600 text-secondary-emphasis'>Batch:</b> Final Year </span> 
                      <span className="badge bg-secondary-subtle text-black font-12 font-500 rounded-pill"><b className='font-600 text-secondary-emphasis'>Placement season:</b> Dec 2025</span> 
                    </div>

                    <div className="row g-2 mb-3">
                        <div className="col-4">
                            <div className="d-flex flex-column align-items-center justify-content-center p-3 bg-white rounded-3 border border-light-subtle h-100">
                                <div className="fw-bold text-primary font-16">{mentee.sessionCountInWindow ?? 0}</div>
                                <div className="text-muted font-12 font-500 text-uppercase tracking-wider">Sessions</div>
                            </div>
                        </div>
                        <div className="col-4">
                            <div className="d-flex flex-column align-items-center justify-content-center p-3 bg-white rounded-3 border border-light-subtle h-100">
                                <div className="fw-bold text-danger font-16">—</div>
                                <div className="text-muted font-12 font-500 text-uppercase tracking-wider">Unread Msgs</div>
                            </div>
                        </div>
                        <div className="col-4">
                            <div className="d-flex flex-column align-items-center justify-content-center p-3 bg-white rounded-3 border border-light-subtle h-100">
                                <div className="fw-semibold text-muted font-14">{lastSession}</div>
                                <div className="text-muted font-12 font-500 text-uppercase tracking-wider">Last Session</div>
                            </div>
                        </div>
                    </div>

                    {dod ? (
                        <p className="mmtees-dod">
                            DoD: {dod.sessionCount}/{dod.sessionMinimum} sessions · {dod.interactionCount}/
                            {dod.interactionMinimum} interactions
                            {dod.isComplete ? ' · complete' : ''}
                        </p>
                    ) : null}

                    {mentee.sessionLinks && mentee.sessionLinks.length > 0 ? (
                        <>
                            <span className="font-14 font-600 text-black mb-1 d-block">Session links:</span>
                            <div className="d-flex g-2">
                                {mentee.sessionLinks.map((link) => (
                                    <a className='btn btn-outline-primary btn-sm rounded-pill px-3' href={link} key={link} target='_blank'>{link}</a>
                                ))}
                            </div>
                        </>
                    ) : null}

                </div>

                

                 

                  <div className="card-footer bg-light border-0 p-3 d-flex flex-wrap gap-2">
                    {uuid ? (
                    <Link
                        to={MentorPortalService.messagesHref(uuid)}
                        className="btn btn-sm btn-primary fw-semibold px-3 text-nowrap rounded-pill"
                    >
                        💬 Message
                    </Link>
                    ) : null}

                    {mentee.canLogOffline && onLog ? (
                    <button type="button" className="btn btn-sm btn-outline-secondary text-nowrap rounded-pill px-3" onClick={onLog}>
                        Log
                    </button>
                    ) : null}
                    <button type="button" className="btn btn-sm btn-outline-secondary fw-semibold text-nowrap rounded-pill" disabled title="Scheduling arrives with Sessions">
                        📅 Schedule Session
                    </button>
                    {profileHref ? (
                        <a className="btn btn-sm btn-outline-secondary fw-semibold text-nowrap rounded-pill " href={profileHref}>
                            📄 View Full Profile
                        </a>
                    ) : (
                        <button type="button" className="btn btn-sm btn-outline-secondary fw-semibold" disabled title="No mentee uuid on GET">
                            📄 View Full Profile
                        </button>
                    )}

                    
                  </div>

                  

           

            </div>

            

           
        </div>
    );
};

export default MentorMenteeCard;
